import {
  AfterContentInit,
  AfterViewInit,
  Component, ContentChildren,
  EventEmitter, HostListener,
  Input, OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef
} from '@angular/core';
import {TranslateService} from "../../../services/multilingual/translate.service";
import {LocalStorageService} from "../../../services/storage/localstorage.service";
import {JBMTableActionClickEvent, JBMTableActionDef, JBMTableColumnDef,
  JBMTableColumnFilterEvent, JBMTableColumnSortEvent, JBMTableColumnType,
  StringIDdisplay} from './types';
import {JBMTableColumnComponent} from './JBMTableColumn/JBMTableColumn.component';
import {Subscription} from 'rxjs';

export * from './types';
export const IDTag = 'id';

@Component({
  selector: 'jbm-table',
  templateUrl: './JBMTable.component.html',
  styleUrls: ['./JBMTable.component.scss']
})
export class JBMTableComponent implements OnInit, OnChanges, AfterContentInit, OnDestroy {
  readonly ColType = JBMTableColumnType;

  rowExpanded: number=-1;

  @Input() id: string='JBMTable';
  @Input() classname: string='JBM-table';
  // Look and feel
  @Input() striped: boolean=false;
  @Input() bordered: boolean=false;
  @Input() size: string='sm';
  @Input() hideHeader: boolean=false;
  @Input() rowExpandTooltip: string='';
  @Input() rowCollapseTooltip: string='';
  @Input() fullWidth: boolean=true;
  @Input() actionsColumnWidth: number=5;
  @Input() stringIDdisplay: StringIDdisplay=StringIDdisplay.none;

  // Behavioral
  @Input() absolute: boolean=false;
  @Input() responsive: boolean=true;
  @Input() hoverable: boolean=false;
  @Input() sortable: boolean=true;
  @Input() filterable: boolean=true;
  @Input() searchMinLength: number=2;
  @Input() showFilters: boolean=false;
  @Input() rowSelectable: boolean=false;
  @Input() highLightSelectedRow: boolean=false;
  @Input() selectedRowId: number=-1;

  // Columns
  @Input() columnDefs: JBMTableColumnDef[]=[];
  @Input() actionDefs: JBMTableActionDef[]=[];
  // Data
  @Input() sortColumn: string='';
  @Input() sortDirection: string='+';
  @Input() data: any[];
  // Events
  @Output() rowCountChange = new EventEmitter();

  @Output() columnDefsChange = new EventEmitter<JBMTableColumnDef[]>();
  @Output() sortChange = new EventEmitter();
  @Output() filterChange = new EventEmitter();
  @Output() rowSelect = new EventEmitter();
  @Output() rowExpand = new EventEmitter();
  @Output() rowCollapse = new EventEmitter();
  @Output() actionClick = new EventEmitter();
  @Output() onDropdownOpenChange = new EventEmitter();
  @Output() toggleRowExpandChange = new EventEmitter();

  @Input() template: TemplateRef<any>;
  @ContentChildren(JBMTableColumnComponent) projectedColumns: QueryList<JBMTableColumnComponent>;

  columnFilterEvent: JBMTableColumnFilterEvent | null = null;
  columnSortEvent: JBMTableColumnSortEvent | null = null;
  actionClickEvent: JBMTableActionClickEvent | null = null;

  private static LocalStoragePrefix='JBMTable';
  private projectedColumnsChange$: Subscription;
  private usingProjectedColumns: boolean=false;

  constructor(
      private LocalStorageService: LocalStorageService,
      private TranslateService: TranslateService
  ) {}

  ngOnInit(): void {
    if(this.columnDefs==undefined) this.columnDefs=[];
    if(this.actionDefs==undefined) this.actionDefs=[];

    // Default min string length of search filter for all table columns
    if(this.searchMinLength==undefined) this.searchMinLength=2;

    if(this.size==undefined) this.size='sm';

    if(this.rowExpandTooltip==undefined) this.rowExpandTooltip=this.TranslateService.GetTranslation('ui.table.row-expand-tooltip');
    if(this.rowCollapseTooltip==undefined) this.rowCollapseTooltip=this.TranslateService.GetTranslation('ui.table.row-collapse-tooltip');

    if(this.template==undefined) this.template=null;

    this.columnDefs=this.normalizeColumnDefs(this.columnDefs);
  }
  ngOnChanges(changes: SimpleChanges) {
    if(changes.hasOwnProperty('columnDefs') && changes.columnDefs.currentValue)
      this.usingProjectedColumns=false;
    if(changes.hasOwnProperty('columnDefs') && changes.columnDefs.currentValue)
      this.columnDefs=this.normalizeColumnDefs(changes.columnDefs.currentValue);

    if(changes.hasOwnProperty('sortColumn'))
      this._setSortedColumn(changes.sortColumn.currentValue);

    if(changes.hasOwnProperty('showFilters') && !changes.showFilters.isFirstChange() && this.absolute)
      // Absolute positioned table, so recalculate rowcount
      // Use SetTimeOut to prevent ExpressionChangedAfterItHasBeenCheckedError
      setTimeout(() => {
        this.rowCountChange.emit(this.getRowCount());
      });
  }
  ngAfterContentInit(): void {
    this.applyProjectedColumns();
    this.projectedColumnsChange$=this.projectedColumns.changes.subscribe(() => this.applyProjectedColumns());
  }
  ngOnDestroy(): void {
    if(this.projectedColumnsChange$!==undefined)
      this.projectedColumnsChange$.unsubscribe();
  }
  private applyProjectedColumns() {
    if(!this.projectedColumns || !this.projectedColumns.length || ((this.columnDefs && this.columnDefs.length) && !this.usingProjectedColumns)) return;
    this.usingProjectedColumns=true;
    this.columnDefs=this.normalizeColumnDefs(this.projectedColumns.map((column: JBMTableColumnComponent) => column.toColumnDef()));
  }
  private normalizeColumnDefs(columnDefs: JBMTableColumnDef[]) {
    if(columnDefs===undefined) return [];
    for(let c=0; c<columnDefs.length; c++) {
      // Hide ID column if set; only for (record) reference purposes
      if(columnDefs[c].type===JBMTableColumnType.id) {
        columnDefs[c].visible = false;
        // Tag as ID column
        columnDefs[c].tag = IDTag;
      }
      if(columnDefs[c].type===JBMTableColumnType.stringAndID && !columnDefs[c].IDName)
        columnDefs[c].IDName = 'id'
      // If header property not set, assume empty header
      if(columnDefs[c].header===undefined)
        columnDefs[c].header='';
      if(columnDefs[c].className===undefined)
        columnDefs[c].className='';
      // If header iconClass property not set, assume no icon in header
      if(columnDefs[c].iconClassname===undefined)
        columnDefs[c].iconClassname='';
      // If columntype property not set, assume string columntype
      if(columnDefs[c].type===undefined)
        columnDefs[c].type=JBMTableColumnType.string;
      // If hideable property not set, assume hideable
      if(columnDefs[c].hideable===undefined)
        columnDefs[c].hideable=true;
      // If visible property not set, assume visible
      if(columnDefs[c].visible===undefined)
        columnDefs[c].visible=true;
      // If sortable property not set, assume not sortable
      if(columnDefs[c].sortable===undefined)
        columnDefs[c].sortable=false;
      if(columnDefs[c].sorted===undefined)
        columnDefs[c].sorted=false;
      // If moveable property not set, assume not moveable
      if(columnDefs[c].moveable===undefined)
        columnDefs[c].moveable=false;
      // If sizeable property not set, assume not sizeable
      if(columnDefs[c].sizeable===undefined)
        columnDefs[c].sizeable=false;
      // If sizeable and minWidth not set, default minWidth to the column width
      if(columnDefs[c].sizeable && !columnDefs[c].minWidth && columnDefs[c].width)
        columnDefs[c].minWidth=columnDefs[c].width;
      // If min string length of search filter not set, assume global table setting
      if(columnDefs[c].searchMinLength===undefined)
        columnDefs[c].searchMinLength=this.searchMinLength;
      // If filterable property not set, assume not filterable
      if(columnDefs[c].filterable===undefined)
        columnDefs[c].filterable=false;
      // If width property not set, width is calculated via the tablecolumn datatype,
      // or is set to auto (width=0); meaning visually controlled by <table> tag
      if(columnDefs[c].width===undefined)
        columnDefs[c].width=this._calcColumnWidth(columnDefs[c].type ?? JBMTableColumnType.string);
    }
    return columnDefs;
  }
  // Event handling
  tableColumnDefsChange(defs: JBMTableColumnDef[]) {
    this.columnDefs = defs;
    this.columnDefsChange.emit(defs);
  }

  tableSortChange(event: any) {
    this.columnSortEvent={ name: event.name, descending: event.descending };
    // Emit event
    this.sortChange.emit( this.columnSortEvent );
  }
  tableFilterChange(event: any) {
    this.columnFilterEvent={ name: event.name, value: event.value };
    this._saveFilterValue(event.name, event.value);
    // Emit event
    this.filterChange.emit( this.columnFilterEvent );
  }
  onRowSelect(rowData: any) {
    if(!this.rowSelectable) return;
    this.selectedRowId=rowData.id;
    this.rowSelect.emit( rowData );
  }
  isExpandable(rowData: any) {
    return !rowData.hasOwnProperty('isExpandable') || (rowData.hasOwnProperty('isExpandable') && rowData.isExpandable);
  }
  visibleColumnCount(): number {
    return this.columnDefs.filter(c => c.visible).length;
  }
  public toggleRowExpanded(rowData: any) {
    if(this.rowExpanded) this.rowCollapse.emit(rowData);
    if(this.rowExpanded==-1) this.rowExpand.emit(rowData);
    this.rowExpanded=this.rowExpanded===rowData.id ? -1 : rowData.id;
    this.toggleRowExpandChange.emit(this.rowExpanded!==1);
  }
  onActionClick(row: any, actionName: any) {
    this.actionClickEvent={ name: actionName, row: row };
    this.actionClick.emit( this.actionClickEvent );
  }

  // State
  saveState(storageKey: string) {
    let key=JBMTableComponent.LocalStoragePrefix+storageKey;
    if(this.LocalStorageService.hasKey(key))
        // Delete actual state data
      this.clearState(storageKey);

    let columns=[];
    for(let i=0; i<this.columnDefs.length; i++)
      if(this.columnDefs[i].filterable && this.columnDefs[i].filterValue!==undefined && this.columnDefs[i].filterValue!=='')
        columns.push( { name: this.columnDefs[i].name, filterValue: this.columnDefs[i].filterValue } );

    this.LocalStorageService.set(key, { columns: columns, sortColumn: this.sortColumn, sortDirection: this.sortDirection });
  }
  restoreState(storageKey: string, clearState: boolean=true) {
    let key=JBMTableComponent.LocalStoragePrefix+storageKey;
    if(!this.LocalStorageService.hasKey(key)) return;
    let state=this.LocalStorageService.get(key);

    this.sortColumn=state.sortColumn;
    this.sortDirection=state.sortDirection;

    for(let cd=0; cd<this.columnDefs.length; cd++)
      for(let i=0; i<state.columns.length; i++)
        if(state.columns[i].name===this.columnDefs[cd].name)
          this.columnDefs[cd].filterValue=state.columns[i].filterValue;

    if(clearState)
      this.clearState(storageKey);
  }
  clearState(storageKey: string) {
    let key=JBMTableComponent.LocalStoragePrefix+storageKey;
    if(this.LocalStorageService.hasKey(key))
      this.LocalStorageService.remove(key);
  }

  /* Service routines */

  setColumnTemplate(columnName: string, template: TemplateRef<any>) {
    let column: JBMTableColumnDef | undefined = this.columnDefs.find((cd: any) => cd.name === columnName)
    if(!column)
      return
    if(!column.template)
      column.template = template
  }

  // Reset filter values and empty filter inputs
  resetColumnFilters() {
    for(let columnDef of this.columnDefs) {
      if(!columnDef.filterable) continue;
      columnDef.filterValue = '';
      (<HTMLInputElement>document.getElementById('columnfilter-'+columnDef.name)).value='';
    }
  }
  // Reset filter value and empty filter input
  resetColumnFilter(name: string) {
    for(let columnDef of this.columnDefs) {
      if(columnDef.name===name && columnDef.filterable) {
        columnDef.filterValue = '';
        (<HTMLInputElement>document.getElementById('columnfilter-'+columnDef.name)).value='';
      }
    }
  }
  resetColumnFilterValues() {
    for(let columnDef of this.columnDefs) {
      if(!columnDef.filterable) continue;
      columnDef.filterValue = '';
    }
  }
  resetColumnFilterValue(name: string) {
    for(let columnDef of this.columnDefs)
      if(columnDef.name===name && columnDef.filterable)
        columnDef.filterValue = '';
  }

  _setSortedColumn(columnName: string) {
    this._resetSortedColumn();
    for(let c=0; c<this.columnDefs.length; c++)
      if(this.columnDefs[c].name===columnName)
        this.columnDefs[c].sorted=true;
  }
  _resetSortedColumn() {
    for(let c=0; c<this.columnDefs.length; c++)
        this.columnDefs[c].sorted=false;
  }
  _isSorted(columnName: string) {
    return this.sortColumn===columnName;
  }

  _saveFilterValue(columnName: string, filterValue: any) {
    for(let c=0; c<this.columnDefs.length; c++)
      if(this.columnDefs[c].name===columnName)
        this.columnDefs[c].filterValue=filterValue;
  }

  _calcColumnWidth(columnType: JBMTableColumnType): number {
    switch (columnType) {
      case JBMTableColumnType.datetime: { return 7.5; }
      case JBMTableColumnType.datetimeseconds: { return 9; }
      case JBMTableColumnType.date: { return 6; }
      case JBMTableColumnType.dateshort: { return 3; }
      case JBMTableColumnType.time: { return 5; }
      case JBMTableColumnType.timeseconds: { return 6.5; }
      case JBMTableColumnType.panelToggle: { return 2; }
      default: { return 0; }
    }
  }

  openDropdownChange(opened: boolean) {
    this.onDropdownOpenChange.emit(opened);
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    if(!this.absolute) return;
    this.rowCountChange.emit(this.getRowCount());
  }
  getRowCount() {
    if(!this.absolute) return 0;
    if(<HTMLDivElement>document.getElementById(this.id)===null) return 0

    // Size dependant table header/row heights
    let rowHeight=40;
    let marge=5;
    if(this.size==='sm') rowHeight=34;
    if(this.size==='xs') {
      rowHeight=32;
      marge=2;
    }

    // Calculate (new) row count
    let height=(<HTMLDivElement>document.getElementById(this.id)).clientHeight;

    // Subtract header and filter row heights
    height-=rowHeight;
    if(this.showFilters) height-=rowHeight;

    return Math.ceil(height / (rowHeight+marge));
  }
}
